import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Save,
  Upload,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeStorageFilename } from "@/lib/storage-filename";
import {
  createDownload,
  createDownloadCategory,
  getDownload,
  getDownloadSignedUrl,
  listDownloadCategories,
  updateDownload,
} from "@/lib/downloads.functions";

export const Route = createFileRoute("/_admin/admin/downloads/$id")({
  head: () => ({
    meta: [
      { title: "Download bearbeiten – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DownloadEditor,
});

const MAX_BYTES = 200 * 1024 * 1024; // 200 MB

function formatBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return "–";
  const units = ["B", "KB", "MB", "GB"];
  let val = n;
  let idx = 0;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx++;
  }
  return `${val.toFixed(val >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function DownloadEditor() {
  const { id } = useParams({ from: "/_admin/admin/downloads/$id" });
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const getFn = useServerFn(getDownload);
  const createFn = useServerFn(createDownload);
  const updateFn = useServerFn(updateDownload);
  const signFn = useServerFn(getDownloadSignedUrl);
  const categoriesFn = useServerFn(listDownloadCategories);
  const createCategoryFn = useServerFn(createDownloadCategory);

  const { data: existing, isLoading } = useQuery({
    queryKey: ["admin-download", id],
    queryFn: () => getFn({ data: { id } }),
    enabled: !isNew,
  });
  const { data: categories } = useQuery({
    queryKey: ["admin-download-categories"],
    queryFn: () => categoriesFn(),
  });

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [file, setFile] = useState<{
    path: string;
    name: string;
    mime: string | null;
    size: number | null;
  } | null>(null);

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const initialised = useRef(false);

  useEffect(() => {
    if (!existing || initialised.current) return;
    initialised.current = true;
    setTitle(existing.title);
    setDescription(existing.description ?? "");
    setCategoryId(existing.category_id);
    setFile({
      path: existing.file_path,
      name: existing.file_name,
      mime: existing.mime_type,
      size: existing.size_bytes,
    });
  }, [existing]);

  async function handleUpload(f: File) {
    setError(null);
    if (f.size > MAX_BYTES) {
      setError(`Datei ist zu groß (max. ${formatBytes(MAX_BYTES)}).`);
      return;
    }
    setUploading(true);
    try {
      const uid =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2);
      const path = `${uid}/${sanitizeStorageFilename(f.name)}`;
      const { error: upErr } = await supabase.storage
        .from("downloads")
        .upload(path, f, { contentType: f.type || "application/octet-stream" });
      if (upErr) throw new Error(upErr.message);
      setFile({
        path,
        name: f.name,
        mime: f.type || null,
        size: f.size,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function onAddCategory() {
    const name = prompt("Name der neuen Kategorie")?.trim();
    if (!name) return;
    try {
      const cat = await createCategoryFn({ data: { name } });
      qc.invalidateQueries({ queryKey: ["admin-download-categories"] });
      setCategoryId(cat.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  async function onPreview() {
    if (!file || isNew) return;
    try {
      const res = await signFn({ data: { id } });
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // Ticket AV6: Die Option „Genau ein Kunde" ist entfallen; jede Datei gilt für
  // alle Agenturkunden. Sichtbarkeit und Kundenzuordnung setzt der Server fest.
  const canSave = !!title.trim() && !!categoryId && !!file;

  async function onSave() {
    setError(null);
    if (!canSave || !file) {
      setError("Bitte alle Pflichtfelder ausfüllen und eine Datei hochladen.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await createFn({
          data: {
            title: title.trim(),
            description: description.trim() || null,
            category_id: categoryId,
            visibility: "all_clients" as const,
            client_id: null,
            file_path: file.path,
            file_name: file.name,
            mime_type: file.mime,
            size_bytes: file.size,
          },
        });
        qc.invalidateQueries({ queryKey: ["admin-downloads"] });
        navigate({ to: "/admin/downloads/$id", params: { id: res.id } });
      } else {
        await updateFn({
          data: {
            id,
            title: title.trim(),
            description: description.trim() || null,
            category_id: categoryId,
            visibility: "all_clients" as const,
            client_id: null,
            file_path: file.path,
            file_name: file.name,
            mime_type: file.mime,
            size_bytes: file.size,
          },
        });
        qc.invalidateQueries({ queryKey: ["admin-downloads"] });
        qc.invalidateQueries({ queryKey: ["admin-download", id] });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Laden …
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <Link
        to="/admin/downloads"
        className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Zurück zur Übersicht
      </Link>

      <h1 className="mt-4 font-display text-3xl font-extrabold md:text-4xl">
        {isNew ? "Neuer Download" : "Download bearbeiten"}
      </h1>

      <div className="mt-8 space-y-6">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Titel
          </span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            placeholder="z. B. Kampagnen-Handbuch 2026"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Beschreibung
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            placeholder="Optional – kurzer Kontext, Zielgruppe, Version."
          />
        </label>

        <div>
          <span className="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span>Kategorie</span>
            <button
              type="button"
              onClick={onAddCategory}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-foreground hover:underline"
            >
              <Plus className="h-3 w-3" /> Neue anlegen
            </button>
          </span>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
          >
            <option value="">– bitte wählen –</option>
            {(categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Datei
          </span>
          {file ? (
            <div className="flex flex-wrap items-center gap-3 rounded border border-border bg-secondary/40 p-3 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-xs">{file.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {formatBytes(file.size)} · {file.mime ?? "unbekannter Typ"}
                </p>
              </div>
              {!isNew && (
                <button
                  type="button"
                  onClick={onPreview}
                  className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-secondary"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Vorschau
                </button>
              )}
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-secondary"
              >
                <Upload className="h-3.5 w-3.5" /> Datei ersetzen
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded border border-dashed border-border p-6 text-sm font-semibold text-muted-foreground hover:bg-secondary"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Hochladen …
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" /> Datei auswählen
                </>
              )}
            </button>
          )}
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
            }}
          />
          {uploading && file && (
            <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Ersetze Datei …
            </p>
          )}
          <p className="mt-1 text-[11px] text-muted-foreground">
            Maximal {formatBytes(MAX_BYTES)}. Datei bleibt privat, Zugriff nur über
            zeitlich begrenzte signierte Links.
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave || saving || uploading}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isNew ? "Anlegen" : "Speichern"}
          </button>
          <Link
            to="/admin/downloads"
            className="text-sm font-semibold text-muted-foreground hover:underline"
          >
            Abbrechen
          </Link>
        </div>
      </div>
    </div>
  );
}
